import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useDiary, pixelY, getViewportHeight, formatTime, BASE_PX_PER_MINUTE, ACTIONS } from './DiaryContext';
import { useDrag } from './DragContext';
import TimeAxis from './TimeAxis';
import EventBlock from './EventBlock';
import SlotPopup from './SlotPopup';
import NonWorkingOverlay from './NonWorkingOverlay';
import { computeOverlapLanes } from './overlapLayout';
import moment from 'moment';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * WeekView - Shows 7 day columns for a single staff member.
 * Each column works like a mini StaffColumn with events, overlaps, etc.
 */
function WeekView() {
  const { state, dispatch, onEventClick, onSlotClick, onBackToDay } = useDiary();
  const {
    selectedStaffId, staffById, selectedDate,
    viewStartMinutes, viewEndMinutes, zoomLevel,
    weekData, weekStaffInfo,
  } = state;

  const scrollRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const prevZoomRef = useRef(zoomLevel);

  const staff = staffById[selectedStaffId];
  const totalHeight = getViewportHeight(viewStartMinutes, viewEndMinutes, zoomLevel);

  // Compute the Monday of the week containing selectedDate
  const weekDays = useMemo(() => {
    const base = moment(selectedDate);
    const monday = base.clone().startOf('isoWeek');
    return Array.from({ length: 7 }, (_, i) => {
      const d = monday.clone().add(i, 'days');
      return {
        key: d.format('YYYY-MM-DD'),
        label: DAY_LABELS[i],
        dateLabel: d.format('D'),
        fullLabel: d.format('ddd D MMM'),
        isToday: d.isSame(moment(), 'day'),
        isSelected: d.format('YYYY-MM-DD') === selectedDate,
      };
    });
  }, [selectedDate]);

  // Keep viewport centred on same time when zoom changes
  useEffect(() => {
    const prev = prevZoomRef.current;
    prevZoomRef.current = zoomLevel;
    if (prev === zoomLevel || !scrollRef.current) return;

    const el = scrollRef.current;
    const centreY = el.scrollTop + el.clientHeight / 2;
    const centreMinutes = viewStartMinutes + centreY / (BASE_PX_PER_MINUTE * prev);
    const newCentreY = (centreMinutes - viewStartMinutes) * BASE_PX_PER_MINUTE * zoomLevel;
    el.scrollTop = newCentreY - el.clientHeight / 2;
  }, [zoomLevel, viewStartMinutes]);

  // Auto-scroll to working start on first load
  useEffect(() => {
    if (hasScrolledRef.current || !scrollRef.current) return;
    hasScrolledRef.current = true;

    if (staff?.isWorking && staff.startMinutes != null) {
      const scrollTarget = Math.max(0, staff.startMinutes - 30);
      const scrollPx = pixelY(scrollTarget, viewStartMinutes, zoomLevel);
      scrollRef.current.scrollTop = scrollPx;
    }
  }, [staff, viewStartMinutes, zoomLevel]);

  const handleBackToDay = () => {
    if (onBackToDay) {
      onBackToDay();
    } else {
      dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: 'day' });
      dispatch({ type: ACTIONS.SET_SELECTED_STAFF, payload: null });
    }
  };

  // Grid line background
  const slotPx = pixelY(15, 0, zoomLevel);
  const hourPx = pixelY(60, 0, zoomLevel);
  const gridBackground = useMemo(() => {
    const minor = `repeating-linear-gradient(to bottom, transparent 0px, transparent ${slotPx - 1}px, #f5f5f7 ${slotPx - 1}px, #f5f5f7 ${slotPx}px)`;
    const major = `repeating-linear-gradient(to bottom, transparent 0px, transparent ${hourPx - 1}px, #ebebed ${hourPx - 1}px, #ebebed ${hourPx}px)`;
    return `${major}, ${minor}`;
  }, [slotPx, hourPx]);

  return (
    <div className="dv2-viewport dv2-viewport--week" ref={scrollRef}>
      <div className="dv2-viewport-inner">
        {/* Time axis with back button in the header area */}
        <div className="dv2-time-axis dv2-time-axis--week">
          <div className="dv2-week-back-header" onClick={handleBackToDay}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="dv2-week-back-name">{getInitials(staff?.name)}</span>
          </div>
          {/* Time labels wrapper - provides positioning context aligned with column bodies */}
          <div className="dv2-week-time-labels">
            <WeekTimeLabels />
          </div>
        </div>
        <div className="dv2-columns-container" style={{ minHeight: totalHeight }}>
          {weekDays.map(day => {
            const dayEvents = weekData?.[day.key] || [];
            const dayStaffInfo = weekStaffInfo?.[day.key];
            // Build a per-day staff object with that day's working hours
            const dayStaff = staff && dayStaffInfo ? {
              ...staff,
              isWorking: dayStaffInfo.isWorking,
              startMinutes: dayStaffInfo.startMinutes,
              endMinutes: dayStaffInfo.endMinutes,
            } : staff;
            return (
              <WeekDayColumn
                key={day.key}
                day={day}
                staff={dayStaff}
                events={dayEvents}
                totalHeight={totalHeight}
                gridBackground={gridBackground}
                slotPx={slotPx}
                hourPx={hourPx}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * WeekTimeLabels - Time labels for the week view time axis.
 * Separate from TimeAxis so we can embed them in the custom week axis.
 */
function WeekTimeLabels() {
  const { state } = useDiary();
  const { viewStartMinutes, viewEndMinutes, zoomLevel, slotDurationMinutes } = state;

  const labels = useMemo(() => {
    const items = [];
    let interval = 60;
    if (zoomLevel >= 2.0 && slotDurationMinutes <= 15) interval = 30;
    if (zoomLevel >= 2.5 && slotDurationMinutes <= 10) interval = 15;
    if (zoomLevel <= 0.75) interval = 120;

    const start = Math.ceil(viewStartMinutes / interval) * interval;
    for (let m = start; m <= viewEndMinutes; m += interval) {
      items.push({ minutes: m, label: formatTime(m), isMajor: m % 60 === 0 });
    }
    return items;
  }, [viewStartMinutes, viewEndMinutes, zoomLevel, slotDurationMinutes]);

  return (
    <>
      {labels.map(item => (
        <div
          key={item.minutes}
          className={`dv2-time-label ${item.isMajor ? 'dv2-time-label--major' : 'dv2-time-label--minor'}`}
          style={{ top: pixelY(item.minutes, viewStartMinutes, zoomLevel) }}
        >
          <span>{item.label}</span>
        </div>
      ))}
    </>
  );
}

// Ghost block duration (matches StaffColumn)
const GHOST_DURATION = 30;

function snapToSlot(minutes, slotDuration) {
  return Math.floor(minutes / slotDuration) * slotDuration;
}

function overlapsEvent(startMin, endMin, events) {
  return events.some(e => startMin < e.endMinutes && endMin > e.startMinutes);
}

/**
 * WeekDayColumn - One day column within the week view.
 * Full parity with StaffColumn: ghost hover, slot popup, drag-and-drop.
 */
function WeekDayColumn({ day, staff, events, totalHeight, gridBackground, slotPx, hourPx }) {
  const { state, onSlotClick, onDayTap } = useDiary();
  const { viewStartMinutes, viewEndMinutes, zoomLevel, slotDurationMinutes } = state;
  const { dragState, registerColumn, lastDragEndRef } = useDrag();
  const [ghostMinutes, setGhostMinutes] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null);
  const [nowMinutes, setNowMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });
  const ghostRef = useRef(null);
  const columnRef = useRef(null);

  // Update now indicator every 60s (only for today's column)
  useEffect(() => {
    if (!day.isToday) return;
    const interval = setInterval(() => {
      const now = new Date();
      setNowMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60000);
    return () => clearInterval(interval);
  }, [day.isToday]);

  // Register this column for drag hit-testing using the day key as ID
  const columnBodyRef = useCallback((el) => {
    columnRef.current = el;
    if (staff) registerColumn(staff.id + '_' + day.key, el);
  }, [staff, day.key, registerColumn]);

  const isDropTarget = dragState && dragState.mode === 'move' && dragState.currentStaffId === (staff?.id + '_' + day.key);
  const dropMinutes = isDropTarget ? dragState.currentMinutes : null;

  const isResizing = dragState && dragState.mode === 'resize' && dragState.originStaffId === (staff?.id + '_' + day.key);
  const resizeEventId = isResizing ? dragState.event.id : null;
  const resizeStartMinutes = isResizing ? dragState.newStartMinutes : null;
  const resizeEndMinutes = isResizing ? dragState.newEndMinutes : null;

  // Stamp events with composite staffId so drag system matches the column registration
  const columnId = staff ? staff.id + '_' + day.key : day.key;
  const stampedEvents = useMemo(() => {
    return events.map(evt => ({ ...evt, staffId: columnId, _originalStaffId: evt.staffId, _dayKey: day.key }));
  }, [events, columnId, day.key]);
  const laidOutEvents = useMemo(() => computeOverlapLanes(stampedEvents), [stampedEvents]);

  const handleColumnClick = useCallback((e) => {
    if (!onSlotClick || !staff) return;
    if (Date.now() - lastDragEndRef.current < 300) return;
    if (e.target.closest('.dv2-event-block')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const minutesFromViewStart = clickY / (BASE_PX_PER_MINUTE * zoomLevel);
    const totalMinutes = viewStartMinutes + minutesFromViewStart;
    const snapped = snapToSlot(totalMinutes, slotDurationMinutes);

    // On touch devices, skip popup and go straight to booking
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (isTouch) {
      onSlotClick(staff, snapped, 'appointment', day.key);
      return;
    }

    setActiveSlot({ minutes: snapped });
    setGhostMinutes(null);
  }, [onSlotClick, staff, viewStartMinutes, zoomLevel, slotDurationMinutes, day.key, lastDragEndRef]);

  const handlePopupAction = useCallback((actionType) => {
    if (!onSlotClick || !staff || !activeSlot) return;
    onSlotClick(staff, activeSlot.minutes, actionType, day.key);
    setActiveSlot(null);
  }, [onSlotClick, staff, activeSlot, day.key]);

  const handlePopupClose = useCallback(() => {
    setActiveSlot(null);
  }, []);

  // Ghost hover handlers (desktop only, hidden when popup is open)
  const handleMouseMove = useCallback((e) => {
    if (activeSlot) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const rawMinutes = viewStartMinutes + y / (BASE_PX_PER_MINUTE * zoomLevel);
    const snapped = snapToSlot(rawMinutes, slotDurationMinutes);

    if (overlapsEvent(snapped, snapped + GHOST_DURATION, events || [])) {
      setGhostMinutes(null);
    } else {
      setGhostMinutes(snapped);
    }
  }, [viewStartMinutes, zoomLevel, slotDurationMinutes, events, activeSlot]);

  const handleMouseLeave = useCallback(() => {
    if (!activeSlot) setGhostMinutes(null);
  }, [activeSlot]);

  // Ghost block position (hover or locked)
  const displayMinutes = activeSlot ? activeSlot.minutes : ghostMinutes;
  const ghostTop = displayMinutes !== null ? pixelY(displayMinutes, viewStartMinutes, zoomLevel) : 0;
  const ghostHeight = pixelY(GHOST_DURATION, 0, zoomLevel);
  const isLocked = !!activeSlot;

  return (
    <div className={`dv2-staff-column ${day.isToday ? 'dv2-week-col--today' : ''}`}>
      {/* Day header */}
      <div className={`dv2-staff-header dv2-week-day-header ${day.isToday ? 'dv2-week-day-header--today' : ''}`} onClick={() => onDayTap && onDayTap(day.key)}>
        <span className="dv2-week-day-label">{day.label}</span>
        <span className={`dv2-week-day-date ${day.isToday ? 'dv2-week-day-date--today' : ''}`}>{day.dateLabel}</span>
      </div>
      <div
        ref={columnBodyRef}
        className={`dv2-column-body ${isDropTarget ? 'dv2-column-body--drop-target' : ''}`}
        style={{
          height: totalHeight,
          backgroundImage: gridBackground,
          backgroundSize: `100% ${slotPx}px, 100% ${hourPx}px`,
          padding: '0 4px',
        }}
        onClick={handleColumnClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <NonWorkingOverlay staff={staff} />
        {day.isToday && (
          <div className="dv2-now-indicator" style={{ top: pixelY(nowMinutes, viewStartMinutes, zoomLevel) }}>
            <div className="dv2-now-line" />
          </div>
        )}
        {laidOutEvents.map(evt => (
          <EventBlock
            key={evt.id}
            event={evt}
            lane={evt.lane}
            laneCount={evt.laneCount}
            overflow={evt.overflow || 0}
            hidden={evt.hidden || false}
            resizeStartMinutes={evt.id === resizeEventId ? resizeStartMinutes : null}
            resizeEndMinutes={evt.id === resizeEventId ? resizeEndMinutes : null}
          />
        ))}
        {/* Drop indicator */}
        {dropMinutes !== null && dragState && (
          <div
            className="dv2-drop-indicator"
            style={{
              top: pixelY(dropMinutes, viewStartMinutes, zoomLevel),
              height: pixelY(dragState.event.endMinutes - dragState.event.startMinutes, 0, zoomLevel),
              color: dragState.event.diaryColour || '#D6D5EC',
              background: dragState.event.diaryColour || '#D6D5EC',
              opacity: 0.85,
            }}
          >
            <span className="dv2-drop-indicator-time">{formatTime(dropMinutes)}</span>
          </div>
        )}
        {/* Ghost block (hover or locked) */}
        {displayMinutes !== null && !dragState && (
          <div
            ref={ghostRef}
            className={`dv2-ghost-block ${isLocked ? 'dv2-ghost-block--locked' : ''}`}
            style={{ top: ghostTop, height: ghostHeight }}
          >
            <span className="dv2-ghost-time">{formatTime(displayMinutes)}</span>
            <svg className="dv2-ghost-plus" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        )}
      </div>
      {/* Slot popup (Booking / Event / Leave) */}
      {activeSlot && (
        <SlotPopup
          ghostRef={ghostRef}
          onAction={handlePopupAction}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
}

export default React.memo(WeekView);
